import React, { useEffect, useState } from 'react';
import { Button, Card, CardBody, CardTitle, Col, Container, Form, Input, Label, Row, FormFeedback, Table } from "reactstrap";
import * as yup from "yup";
import { useFormik } from "formik";
import Breadcrumb from 'Components/UI/Breadcrumb';
import Editor from 'Components/Form/Editor/Editor';
import Select from "react-select";
import { getAllCategories } from 'helpers/API Calls/Operations/category';
import { getProductById, getProductDimensions, getProductTypes, getSurfaceTypes, updateProduct } from 'helpers/API Calls/Operations/Products';
import Spinners from "Components/UI/Spinner";
import { useNavigate, useParams } from 'react-router-dom';
import { STATUS_OPTIONS } from 'pages/Categories/data';
import ImageGallery from './Media/ImageGallery';
import Hint from 'Components/UI/Hint';

interface ProductFormValues {
  product_name: string;
  product_type: any;
  youtube_url: string;
  admin_notes: string;
  booking_per_day: number;
  main_category: any;
  is_featured: boolean;
  is_public: boolean;
  is_child_suitable: boolean;
  is_adult_suitable: boolean;
  is_seo: boolean;
  seo_title: string;
  seo_description: string;
  categories: any[];
  surface_types: any[];
  dimensions: any;
  base_price: number;
  is_on_offer: boolean;
  discounted_price: number | null;
  status: any;
  requires_cleaning_deposit: boolean;
}

interface DimensionOption {
  label: string;
  value: number;
}

function EditProduct() {

  const navigate = useNavigate();
  const { id } = useParams();
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [productData, setProductData] = useState<any>(null);
  const [dropdownOptions, setDropdownOptions] = useState<{
    categoryOptions: any[];
    productTypeOptions: any[];
    surfaceTypesOptions: any[];
    DimensionsOptions: DimensionOption[];
  }>({
    categoryOptions: [],
    productTypeOptions: [],
    surfaceTypesOptions: [],
    DimensionsOptions: [],
  });

  const formik = useFormik<ProductFormValues>({
    initialValues: {
      product_name: '',
      product_type: null,
      youtube_url: '',
      admin_notes: '',
      booking_per_day: 0,
      main_category: null,
      is_featured: false,
      is_public: false,
      is_child_suitable: false,
      is_adult_suitable: false,
      is_seo: false,
      seo_title: '',
      seo_description: '',
      categories: [],
      surface_types: [],
      dimensions: {},
      base_price: 0,
      is_on_offer: false,
      discounted_price: null,
      status: null,
      requires_cleaning_deposit: false,
    },
    validationSchema: yup.object().shape({
      product_name: yup.string().required('Product name is required'),
      product_type: yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      }).nullable().required('Please select product type'),
      youtube_url: yup.string().url('Please enter a valid URL'),
      booking_per_day: yup.number().min(0, 'Booking per day must be positive'),
      main_category: yup.object().shape({
        label: yup.string().required(),
        value: yup.string().required()
      }).nullable().required('Please select main category'),
      seo_title: yup.string().when('is_seo', { is: true, then: schema => schema.required('SEO title is required when SEO is enabled')}),
      seo_description: yup.string().when('is_seo', {is: true, then: schema => schema.required('SEO description is required when SEO is enabled')}),
      categories: yup.array().of( yup.object().shape({label: yup.string().required(),value: yup.string().required()})),
      surface_types: yup.array().of( yup.object().shape({label: yup.string().required(),value: yup.string().required()})),
      dimensions: yup.object(),
      base_price: yup.number().min(0, 'Price must be positive').required('Price is required'),
      discounted_price: yup.number().nullable().when('is_on_offer', {
        is: true,
        then: schema => schema
          .required('Discounted price is required when on offer')
          .min(0, 'Discounted price must be positive')
          .test('less-than-base', 'Discounted price must be less than base price', 
            function(value) {
              return value < this.parent.base_price;
            }
          )
      }), 
      status : yup.object().shape({label: yup.string().required(), value: yup.string().required(),}).nullable().required('Please select status'),
    }),
    onSubmit: async (values: any, { setSubmitting }) => {
      setSubmitting(true);
      // Transform dimensions object into array format
      const productDimensions = dropdownOptions.DimensionsOptions
        .map(dimensionType => {
          const dimensionKey = dimensionType.label.toLowerCase().replace(/\s+/g, '_');
          const dimensionValue = values.dimensions[dimensionKey];
          
          if (!dimensionValue) return null;

          return {
            dimension_type_id: dimensionType.value,
            size_1: Number(dimensionValue.size1) || 0,
            size_2: Number(dimensionValue.size2) || 0,
            height: Number(dimensionValue.height) || 0
          };
        })
        .filter(dim => dim !== null && (dim.size_1 || dim.size_2 || dim.height));

      const productData = {
        product_name: values.product_name,
        product_type_id: values.product_type?.value,
        youtube_url: values.youtube_url,
        admin_notes: values.admin_notes,
        booking_per_day: values.booking_per_day,
        main_category_id: values.main_category?.value,
        is_featured: values.is_featured ? 1 : 0,
        is_public: values.is_public ? 1 : 0,
        is_child_suitable: values.is_child_suitable ? 1 : 0,
        is_adult_suitable: values.is_adult_suitable ? 1 : 0,
        description: description,
        is_seo: values.is_seo ? 1 : 0,
        seo_title: values.seo_title,
        seo_description: values.seo_description,
        categories: values.categories.map((category) => ({category_id: category.value})),
        surface_types: values.surface_types.map((surface) => ({surface_type_id: surface.value})),
        product_dimensions : productDimensions, 
        product_price: {
          price: values.base_price,
          is_sale_item : values.is_on_offer ? 1 : 0,
          sale_price: values?.discounted_price || 0,
          requires_cleaning_deposit: values?.requires_cleaning_deposit, 
        },
        status: values.status.value,
    };

    const response = await updateProduct(Number(id), productData);
    setSubmitting(false);
    if(response.success){
        navigate('/products');
    }
    }
  });

  // Add scroll to error effect
  useEffect(() => {
    if (Object.keys(formik.errors)?.length > 0 && formik.isSubmitting) {
      const firstErrorKey = Object.keys(formik.errors)[0];
      const element = document.getElementById(firstErrorKey);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth', block: 'center' });
        element.focus();
      }
    }
  }, [formik.errors, formik.isSubmitting]);

  useEffect(() => {
    const getData = async () => {
      setIsLoading(true);

      let categoryOptions = [];
      let productTypeOptions = [];
      let surfaceTypesOptions = [];
      let DimensionsOptions = [];

      const categories = await getAllCategories();
      if(categories.success) {
        categoryOptions = categories.data?.items?.map((category: any) => ({label: category.category_name, value: category.id}));
      }

      const productTypes = await getProductTypes();
      if(productTypes.success) {
        productTypeOptions = productTypes.data.map((type: any) => ({label: type.product_type_name, value: type.id}));
      }

      const surfaceTypes = await getSurfaceTypes();
      if(surfaceTypes.success) {
        surfaceTypesOptions = surfaceTypes.data.map((surface: any) => ({label: surface.surface_type_name, value: surface.id}));
      }

      const dimensions = await getProductDimensions();
      if(dimensions.success) {
        DimensionsOptions = dimensions.data.map((dimension: any) => ({label: dimension.dimension_type, value: dimension.id}));
      }

      setDropdownOptions({
        categoryOptions,
        productTypeOptions,
        surfaceTypesOptions,
        DimensionsOptions
      });

      try {
        const productResponse = await getProductById(Number(id)); 
        if(productResponse.success) {
          const productData = productResponse?.data; 
          setProductData(productData);
          const productType = {label: productData?.product_type?.product_type_name, value: productData?.product_type_id};
          const mainCategory = productData?.main_category ? {label: productData?.main_category?.category_name, value: productData?.main_category_id} : null;
          const categories = productData?.categories?.map((cat: any) => ({label: cat?.category_name, value: cat?.pivot?.category_id}));
          const surfaceTypes = productData?.surface_types?.map((surface: any) => ({label: surface.surface_type_name, value: surface?.pivot?.surface_type_id}));
          const dimensions = productData?.dimension_types?.reduce((acc: any, dim: any) => {
            const key = dim.dimension_type.toLowerCase().replace(/\s+/g, '_');
            acc[key] = {
              size1: dim.pivot.size_1 || 0,
              size2: dim.pivot.size_2 || 0,
              height: dim.pivot.height || 0
            };
            return acc;
          }, {});

          formik.setValues({
            product_name: productData?.product_name || '',
            product_type: productType,
            youtube_url: productData?.youtube_url || '',
            admin_notes: productData?.admin_notes || '',
            booking_per_day: productData?.booking_per_day || 0,
            main_category: mainCategory,
            is_featured: productData?.is_featured === 1,
            is_public: productData?.is_public === 1,
            is_child_suitable: productData?.is_child_suitable === 1,
            is_adult_suitable: productData?.is_adult_suitable === 1,
            is_seo: productData?.is_seo === 1,
            seo_title: productData?.seo_title || '',
            seo_description: productData?.seo_description || '',
            categories: categories,
            surface_types: surfaceTypes,
            dimensions: dimensions,
            base_price: productData?.product_price?.price || 0,
            is_on_offer: productData?.product_price?.is_sale_item === 1,
            discounted_price: productData?.product_price?.sale_price || null,
            status: {label: productData?.status === 'active' ? 'Active' : 'Inactive', value: productData?.status},
            requires_cleaning_deposit: productData?.product_price?.requires_cleaning_deposit ? true : false
          });
          
          setDescription(productData?.description || '');
        }
      } catch (error) {
        console.error('Error fetching product:', error);
      }

      setIsLoading(false);
    };

    getData();
  }, [id]);

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumb items={[{ label: "Products", link: "/products" }, { label: 'Edit' }]} />
        {isLoading ? <div className="position-relative"><Spinners/></div>
        :
        <>
        <Card>
          <CardBody>
            <Form onSubmit={formik.handleSubmit} autoComplete="off">
              {/* Basic Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Basic Information</CardTitle>
                {/* name and type */}
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="product_name">Product Name <span className="text-danger">*</span></Label>
                      <Input
                        id="product_name"
                        name="product_name"
                        type="text"
                        placeholder="Enter product name"
                        value={formik.values.product_name}
                        onChange={formik.handleChange}
                        invalid={formik.touched.product_name && formik.errors.product_name ? true : false}
                      />
                      {formik.errors.product_name && formik.touched.product_name && (
                        <FormFeedback type="invalid">{formik.errors.product_name}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="product_type">Product Type <span className="text-danger">*</span></Label>
                      <Select
                        id="product_type"
                        name="product_type"
                        placeholder="Select product type"
                        options={dropdownOptions.productTypeOptions}
                        value={formik.values.product_type}
                        onChange={(value) => formik.setFieldValue("product_type", value)}
                        onBlur={formik.handleBlur}
                        className={formik.touched.product_type && formik.errors.product_type ? "is-invalid" : ""}
                      />
                      {formik.touched.product_type && formik.errors.product_type && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.product_type === 'string' && formik.errors.product_type}
                        </div>
                      )}
                    </div>
                  </Col>
                </Row>

                  {/* youtube url, bookings per day */}
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="youtube_url">YouTube URL</Label>
                      <Input
                        id="youtube_url"
                        name="youtube_url"
                        type="text"
                        placeholder="Enter YouTube URL"
                        value={formik.values.youtube_url}
                        onChange={formik.handleChange}
                        invalid={formik.touched.youtube_url && formik.errors.youtube_url ? true : false}
                      />
                      {formik.errors.youtube_url && formik.touched.youtube_url && (
                        <FormFeedback type="invalid">{formik.errors.youtube_url}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="booking_per_day">Bookings Per Day</Label>
                      <Input
                        id="booking_per_day"
                        name="booking_per_day"
                        type="number"
                        placeholder="Enter bookings per day"
                        value={formik.values.booking_per_day}
                        onChange={formik.handleChange}
                        invalid={formik.touched.booking_per_day && formik.errors.booking_per_day ? true : false}
                      />
                      {formik.errors.booking_per_day && formik.touched.booking_per_day && (
                        <FormFeedback type="invalid">{formik.errors.booking_per_day}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>

                {/* admin notes */}
                <div className="mb-3">
                  <Label htmlFor="admin_notes">Admin Notes</Label>
                  <Input
                    id="admin_notes"
                    name="admin_notes"
                    type="textarea"
                    rows="4"
                    placeholder="Enter admin notes"
                    value={formik.values.admin_notes}
                    onChange={formik.handleChange}
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Pricing</CardTitle>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="base_price">Price <span className="text-danger">*</span></Label>
                      <Input
                        id="base_price"
                        name="base_price"
                        type="number"
                        step="1"
                        placeholder="Enter price"
                        value={formik.values.base_price}
                        onChange={formik.handleChange}
                        invalid={formik.touched.base_price && formik.errors.base_price ? true : false}
                      />
                      {formik.errors.base_price && formik.touched.base_price && (
                        <FormFeedback type="invalid">{formik.errors.base_price}</FormFeedback>
                      )}
                    </div>
                    
                    <div className="form-check mb-3">
                      <Input
                        type="checkbox"
                        className="form-check-input"
                        id="is_on_offer"
                        name="is_on_offer"
                        checked={formik.values.is_on_offer}
                        onChange={formik.handleChange}
                      />
                      <Label className="form-check-label" htmlFor="is_on_offer">
                        On Offer
                      </Label>
                    </div>

                    {formik.values.is_on_offer && (
                      <div className="mb-3">
                        <Label htmlFor="discounted_price">Discounted Price</Label>
                        <Input
                          id="discounted_price"
                          name="discounted_price"
                          type="number"
                          // step="0.01"
                          placeholder="Enter discounted price"
                          value={formik.values.discounted_price || ''}
                          onChange={formik.handleChange}
                          invalid={formik.touched.discounted_price && formik.errors.discounted_price ? true : false}
                        />
                        {formik.errors.discounted_price && formik.touched.discounted_price && (
                          <FormFeedback type="invalid">{formik.errors.discounted_price}</FormFeedback>
                        )}
                      </div>
                    )}
                  </Col>
                  <Col sm={6}>
                    <div className="mb-4">
                      <Label className="form-label">Status <span className="text-danger">*</span></Label>
                      <Select
                        name="status"
                        placeholder="Select Status"
                        options={STATUS_OPTIONS}
                        value={formik.values.status}
                        onChange={(value) => formik.setFieldValue("status", value)}
                        onBlur={formik.handleBlur}
                        className={formik.touched.status && formik.errors.status ? "is-invalid" : ""}
                      />
                      {formik.touched.status && formik.errors.status ? (
                        <FormFeedback type="invalid">{typeof formik.errors.status === 'string' && formik.errors.status}</FormFeedback>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Categories */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Categories</CardTitle>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="main_category">Main Category <span className="text-danger">*</span></Label>
                      <Select
                        id="main_category"
                        name="main_category"
                        placeholder="Select main category"
                        options={dropdownOptions.categoryOptions}
                        value={formik.values.main_category}
                        onChange={(value) => {
                          formik.setFieldValue("main_category", value);
                          if (value) {
                            const existingCategories = formik.values.categories || [];
                            if (!existingCategories.some((cat) => cat.value === value.value)) {
                            formik.setFieldValue("categories", [...existingCategories, value]);}
                          }
                        }}
                        onBlur={formik.handleBlur}
                        className={formik.touched.main_category && formik.errors.main_category ? "is-invalid" : ""}
                      />
                      {formik.touched.main_category && formik.errors.main_category && (
                        <div className="invalid-feedback d-block">
                          {typeof formik.errors.main_category === 'string' && formik.errors.main_category}
                        </div>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="categories">Additional Categories</Label>
                      <Select
                        id="categories"
                        name="categories"
                        placeholder="Select categories"
                        options={dropdownOptions.categoryOptions}
                        value={formik.values.categories}
                        onChange={(value) => formik.setFieldValue("categories", value)}
                        isMulti
                      />
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Product Settings */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Product Settings</CardTitle>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_featured"
                          name="is_featured"
                          checked={formik.values.is_featured}
                          onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="is_featured">
                          Featured Product
                        </Label>
                      </div>
                      <div className="form-check mb-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_public"
                          name="is_public"
                          checked={formik.values.is_public}
                          onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="is_public">
                          Public Product
                        </Label>
                      </div>
                      <div className="form-check mb-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="requires_cleaning_deposit"
                          name="requires_cleaning_deposit"
                          checked={formik.values.requires_cleaning_deposit}
                          onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="requires_cleaning_deposit">
                          Requires Cleaning Deposit
                        </Label>
                      </div>
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <div className="form-check mb-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_child_suitable"
                          name="is_child_suitable"
                          checked={formik.values.is_child_suitable}
                          onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="is_child_suitable">
                          Child Suitable
                        </Label>
                      </div>
                      <div className="form-check mb-2">
                        <Input
                          type="checkbox"
                          className="form-check-input"
                          id="is_adult_suitable"
                          name="is_adult_suitable"
                          checked={formik.values.is_adult_suitable}
                          onChange={formik.handleChange}
                        />
                        <Label className="form-check-label" htmlFor="is_adult_suitable">
                          Adult Suitable
                        </Label>
                      </div>
                    </div>
                  </Col>
                </Row>
              </div>

              {/* Description */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Description</CardTitle>
                <div className="mb-3">
                  <Editor content={description} setContent={setDescription} />
                </div>
              </div>

              {/* Surface Types & Dimensions */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>Surface Types & Dimensions</CardTitle>
                <Row>
                  <Col sm="12">
                    <div className="mb-3">
                      <Label htmlFor="surface_types">Surface Types</Label>
                      <Select
                        id="surface_types"
                        name="surface_types"
                        placeholder="Select surface types"
                        options={dropdownOptions.surfaceTypesOptions}
                        value={formik.values.surface_types}
                        onChange={(value) => formik.setFieldValue("surface_types", value)}
                        isMulti
                      />
                    </div>
                  </Col>
                </Row>

                <div className="table-responsive">
                  <Table className="mb-0">
                    <thead>
                      <tr>
                        <th></th>
                        <th>Size 1</th>
                        <th>Size 2</th>
                        <th>Height</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dropdownOptions.DimensionsOptions.map((dimension) => {
                        const dimensionKey = dimension.label.toLowerCase().replace(/\s+/g, '_');
                        return (
                          <tr key={dimension.value}>
                            <td>{dimension.label}:</td>
                            <td>
                              <Input
                                type="number"
                                step="0.01"
                                name={`dimensions.${dimensionKey}.size1`}
                                value={formik.values.dimensions[dimensionKey]?.size1 || ''}
                                onChange={formik.handleChange}
                                invalid={!!(formik.touched.dimensions?.[dimensionKey]?.size1 && formik.errors.dimensions?.[dimensionKey]?.size1)}
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                step="0.01"
                                name={`dimensions.${dimensionKey}.size2`}
                                value={formik.values.dimensions[dimensionKey]?.size2 || ''}
                                onChange={formik.handleChange}
                                invalid={!!(formik.touched.dimensions?.[dimensionKey]?.size2 && formik.errors.dimensions?.[dimensionKey]?.size2)}
                              />
                            </td>
                            <td>
                              <Input
                                type="number"
                                step="0.01"
                                name={`dimensions.${dimensionKey}.height`}
                                value={formik.values.dimensions[dimensionKey]?.height || ''}
                                onChange={formik.handleChange}
                                invalid={!!(formik.touched.dimensions?.[dimensionKey]?.height && formik.errors.dimensions?.[dimensionKey]?.height)}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* SEO Information */}
              <div className="mb-4">
                <CardTitle tag="h4" className='mb-4'>SEO Information</CardTitle>
                <div className="mb-3">
                  <div className="form-check mb-3">
                    <Input
                      type="checkbox"
                      className="form-check-input"
                      id="is_seo"
                      name="is_seo"
                      checked={formik.values.is_seo}
                      onChange={formik.handleChange}
                    />
                    <Label className="form-check-label" htmlFor="is_seo">
                      Index this product in search engines <Hint type="index" />
                    </Label>
                  </div>
                </div>
                <Row>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="seo_title">Meta Title {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="title" /></Label>
                      <Input
                        id="seo_title"
                        name="seo_title"
                        type="text"
                        placeholder="Enter Meta title"
                        value={formik.values.seo_title}
                        onChange={formik.handleChange}
                        invalid={formik.touched.seo_title && formik.errors.seo_title ? true : false}
                      />
                      {formik.errors.seo_title && formik.touched.seo_title && (
                        <FormFeedback type="invalid">{formik.errors.seo_title}</FormFeedback>
                      )}
                    </div>
                  </Col>
                  <Col sm="6">
                    <div className="mb-3">
                      <Label htmlFor="seo_description">Meta Description {formik.values.is_seo && <span className="text-danger">*</span>} <Hint type="description" /> </Label>
                      <Input
                        id="seo_description"
                        name="seo_description"
                        type="textarea"
                        rows="3"
                        placeholder="Enter Meta description"
                        value={formik.values.seo_description}
                        onChange={formik.handleChange}
                        invalid={formik.touched.seo_description && formik.errors.seo_description ? true : false}
                      />
                      {formik.errors.seo_description && formik.touched.seo_description && (
                        <FormFeedback type="invalid">{formik.errors.seo_description}</FormFeedback>
                      )}
                    </div>
                  </Col>
                </Row>
              </div>

              <div className="d-flex mb-3 justify-content-end">
                <Button type="submit" color="primary" disabled={formik.isSubmitting}>
                  {formik.isSubmitting ? 'Updating...' : 'Update Product'}
                </Button>
              </div>

            </Form>
          </CardBody>
        </Card>
        <Card>
          <CardBody>
          <Row style={{ overflowX: "auto" }}>
            <CardTitle tag="h4" className='mb-4'>Product Media</CardTitle>
            <ImageGallery productId={Number(id)} files={productData?.files} req_type='product'/>
          </Row>
          </CardBody>
        </Card>
        </>
        }
      </Container>
    </div>
  );
}

export default EditProduct;